import React from 'react';
import {CircularProgress, Grid, makeStyles, Typography, useMediaQuery} from '@material-ui/core';
import {useTheme} from '@material-ui/core/styles';
import RhymeCard from './RhymeCard';
import {getRhymeType, getSections} from './rhymeTypes';

const useStyles = makeStyles((theme) => ({
    grid: {
        marginTop: theme.spacing(2),
    },
    error: {
        color: theme.palette.error.main,
    },
    sectionTitle: {
        marginTop: theme.spacing(2),
        color: theme.palette.text.secondary,
        fontWeight: 500,
    },
}));

function Message({className, children}) {
    const classes = useStyles();
    return (
        <Grid container spacing={2} className={classes.grid}>
            <Grid item xs={12}>
                <p className={className} role="status">{children}</p>
            </Grid>
        </Grid>
    );
}

function RhymeList({rhymes, rhymeType, isLoading, error, hasSearched}) {
    const classes = useStyles();
    // No ThemeProvider in this app: useTheme() from core/styles falls back to
    // the default theme, while a callback to useMediaQuery would get null.
    const theme = useTheme();
    const isPhone = useMediaQuery(theme.breakpoints.down('xs'));

    if (isLoading) {
        return (
            <Grid container spacing={isPhone ? 1 : 2} className={classes.grid}>
                <Grid item xs={12}>
                    <CircularProgress aria-label="Searching for rhymes"/>
                </Grid>
            </Grid>
        );
    }

    if (error) {
        return <Message className={classes.error}>{error}</Message>;
    }

    // Before the first search there is nothing to report. The old code fell
    // through to "No rhymes found or an error occurred." here, so a first-time
    // visitor was greeted by an error message on an untouched page.
    if (!hasSearched) {
        return null;
    }

    if (!rhymes || typeof rhymes !== 'object' || !rhymes.typeToResultMap) {
        return <Message className={classes.error}>No rhymes found or an error occurred.</Message>;
    }

    const sections = getSections(rhymes.typeToResultMap, rhymeType);

    if (sections.length === 0) {
        return <Message>No rhymes found.</Message>;
    }

    // Long results (proverbs) get a full row each; words go in a 2/3 column grid.
    const wide = Boolean(getRhymeType(rhymeType).wide);
    const itemProps = wide ? {xs: 12} : {xs: 6, md: 4};

    return (
        <div>
            {sections.map(section => (
                <section key={section.bucket} aria-label={section.title || undefined}>
                    {section.title && (
                        <Typography variant="subtitle2" component="h2" className={classes.sectionTitle}>
                            {section.title}
                        </Typography>
                    )}
                    <Grid container spacing={isPhone ? 1 : 2} className={classes.grid} component="ul"
                          style={{listStyle: 'none', padding: 0}}>
                        {section.items.map((rhyme, index) => (
                            <Grid item {...itemProps} key={`${rhyme}-${index}`} component="li">
                                <RhymeCard rhyme={rhyme} wide={wide}/>
                            </Grid>
                        ))}
                    </Grid>
                </section>
            ))}
        </div>
    );
}

export default RhymeList;
